//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr } from '@shared/SchemaTypes'
import React, { ReactElement, useEffect, useState } from 'react'

import { Box, Grid, Typography } from '@mui/material'
import { OtStoreState, useOtChainState, useOtSelector } from '@react/redux/store'
import {
  PoolDynamicOverviewCTO,
  PoolFlexOverviewCTO,
  PoolFlexOverviewCTOSchema,
  PoolOverviewCTO,
  PoolOverviewCTOSchema,
} from '@shared/sharedTypes'
import { useParams } from 'react-router-dom'

import { FileCopy } from '@mui/icons-material'
import IconButton from '@mui/material/IconButton'
import { IPoolBaseAbi } from '@shared/Abis'
import { otAssert } from '@shared/conversions'
import { PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { LoanCTOSchema } from '@shared/LoanCTO'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { Contract } from 'ethers'
import { SchemaDescription } from 'yup'
import { logOT } from '@shared/loggingShared'

interface ContractAddrListItem {
  label: string
  value: string
}

export default function PoolContractAddresses({ role }: { role: RoleEnum }): React.ReactElement {
  const poolAddr = useParams().poolAddr as EthAddr
  const poolBaseOverviewCTO = useOtSelector(
    (state: OtStoreState) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  )

  const {
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()
  const {
    chainConfiguration: { perimeterAddresses },
  } = useOtChainState()
  const [contractAddresses, setContractAddresses] = useState<ContractAddrListItem[]>([])
  otAssert(perimeterAddresses, 'perimeterAddresses is not defined')
  const poolOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term ? (poolBaseOverviewCTO as PoolOverviewCTO) : null
  const poolFlexOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate ? (poolBaseOverviewCTO as PoolFlexOverviewCTO) : null
  const poolDynamicOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Dynamic ? (poolBaseOverviewCTO as PoolDynamicOverviewCTO) : null

  const handleCopyClick = (text: string): void => {
    navigator.clipboard.writeText(text).catch((_e) => {})
  }
  useEffect(() => {
    const fetchContractAddresses = async () => {
      logOT.trace('fetchContractAddresses', role, !!poolOverviewCTO, !!poolFlexOverviewCTO, !!poolDynamicOverviewCTO)
      const _contractAddresses: ContractAddrListItem[] = [
        {
          label: 'Loan Vault Address',
          value: poolBaseOverviewCTO.poolAddr,
        },
        {
          label: 'Liquidity Asset',
          value: poolBaseOverviewCTO.liquidityAssetAddr,
        },
      ]

      if (role !== RoleEnum.LENDER) {
        if (poolOverviewCTO) {
          _contractAddresses.push({
            label: 'Active ' + ((LoanCTOSchema.describe().fields.fundingVaultAddr as SchemaDescription).label ?? ''),
            value: poolOverviewCTO.activeLoanState.fundingVaultAddr,
          })
        }
        if (poolOverviewCTO) {
          _contractAddresses.push({
            label: 'Requested ' + ((LoanCTOSchema.describe().fields.fundingVaultAddr as SchemaDescription).label ?? ''),
            value: poolOverviewCTO.requestedLoanState.fundingVaultAddr,
          })
        }
        if (poolOverviewCTO || poolFlexOverviewCTO) {
          _contractAddresses.push({
            label: (PoolOverviewCTOSchema.describe().fields.withdrawControllerAddr as SchemaDescription).label ?? '',
            value: poolOverviewCTO?.withdrawControllerAddr ?? poolFlexOverviewCTO?.withdrawControllerAddr ?? '',
          })
        }
        if (poolOverviewCTO || poolFlexOverviewCTO) {
          _contractAddresses.push({
            label: (PoolOverviewCTOSchema.describe().fields.feeVaultAddr as SchemaDescription).label ?? '',
            value: poolOverviewCTO?.feeVaultAddr ?? poolFlexOverviewCTO?.feeVaultAddr ?? '',
          })
        }

        _contractAddresses.push({
          label: (PoolOverviewCTOSchema.describe().fields.poolControllerAddr as SchemaDescription).label ?? '',
          value: poolBaseOverviewCTO.poolControllerAddr,
        })
        _contractAddresses.push({
          label: (PoolOverviewCTOSchema.describe().fields.poolAdminAddr as SchemaDescription).label ?? '',
          value: poolBaseOverviewCTO.poolAdminAddr,
        })
        _contractAddresses.push({
          label: (PoolOverviewCTOSchema.describe().fields.borrowerManagerAddr as SchemaDescription).label ?? '',
          value: poolBaseOverviewCTO.borrowerManagerAddr,
        })
        if (poolOverviewCTO || poolFlexOverviewCTO) {
          _contractAddresses.push({
            label: (PoolOverviewCTOSchema.describe().fields.borrowerVaultAddr as SchemaDescription).label ?? '',
            value: poolOverviewCTO?.borrowerVaultAddr ?? poolFlexOverviewCTO?.borrowerVaultAddr ?? '',
          })
        }
        _contractAddresses.push({
          label: (PoolOverviewCTOSchema.describe().fields.borrowerWalletAddr as SchemaDescription).label ?? '',
          value: poolBaseOverviewCTO.borrowerWalletAddr,
        })
        if (poolOverviewCTO) {
          _contractAddresses.push({
            label: 'Active ' + ((LoanCTOSchema.describe().fields.loanAddr as SchemaDescription).label ?? ''),
            value: poolOverviewCTO.activeLoanState.loanAddr,
          })
        }
        if (poolOverviewCTO) {
          _contractAddresses.push({
            label: 'Requested ' + ((LoanCTOSchema.describe().fields.loanAddr as SchemaDescription).label ?? ''),
            value: poolOverviewCTO.requestedLoanState.loanAddr,
          })
        }
        if (poolFlexOverviewCTO) {
          _contractAddresses.push({
            label:
              (PoolFlexOverviewCTOSchema.describe().fields.businessDayRegistryAddr as SchemaDescription).label ?? '',
            value: poolFlexOverviewCTO.businessDayRegistryAddr,
          })
        }
        _contractAddresses.push({
          label: 'Service Configuration Address',
          value: perimeterAddresses.ServiceConfiguration,
        })
        try {
          const poolBaseContract = new Contract(poolBaseOverviewCTO.poolAddr, IPoolBaseAbi, providerReadOnly)
          const poolAccessControl = await poolBaseContract.poolAccessControl()

          _contractAddresses.push({
            label: 'Pool Access Control Address',
            value: poolAccessControl,
          })
        } catch (e) {
          logOT.error('error', e)
        }
      }

      setContractAddresses(_contractAddresses)
    }
    fetchContractAddresses()
  }, [poolBaseOverviewCTO, role, poolOverviewCTO, poolFlexOverviewCTO, poolDynamicOverviewCTO])

  const gridItems: ReactElement[] = []
  contractAddresses.forEach((addr) => {
    gridItems.push(
      <Grid item xs={4} key={addr.label}>
        <Typography variant="body1" align={'left'} component="div" sx={{ paddingTop: '5px', flexGrow: 1 }}>
          {addr.label}
        </Typography>
      </Grid>
    )
    gridItems.push(
      <Grid item xs={8} key={addr.label + '2'}>
        <div
          style={{
            justifyContent: 'center',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="body1" component="div" sx={{ flexGrow: 1 }} align="left">
            {addr.value}
          </Typography>
          <IconButton
            onClick={() => {
              handleCopyClick(addr.value)
            }}
          >
            <FileCopy />
          </IconButton>
        </div>
      </Grid>
    )
  })

  return (
    <Box>
      <Grid container spacing={1}>
        {gridItems}
      </Grid>
    </Box>
  )
}
